import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  normalBreakpoint,
  smallBreakpoint,
  xsmallBreakpoint,
  xxxsmallBreakpoint,
} from '@epicuro-next/ataraxia/image';
import { MediaPreviewComponent } from '@epicuro-next/ataraxia/media-preview';
import { MediaObjectFit } from '@epicuro-next/ataraxia/models';
import { dynamicElementsListMedia } from '@epicuro-next/constants/dynamic-elements';
import { Media, MediaType } from '@epicuro-next/models/engagement';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { isNonNullish } from '@epicuro-next/utilities';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { debounceTime, filter, map, pluck, takeUntil } from 'rxjs/operators';

const ROOT_ELEMENT = 'epc-root';
// 61 is the of the navigation bar
const NAVIGATION_BAR_HEIGHT = 61;

@DynamicComponentRegister(dynamicElementsListMedia, true)
@Component({
  selector: 'epc-dynamic-elements-list-media',
  templateUrl: './dynamic-elements-list-media.component.html',
  styleUrls: ['./dynamic-elements-list-media.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DynamicElementsListMediaComponent implements OnInit, OnDestroy {
  @Output()
  public epicShowFullScreenChanged = new EventEmitter();

  @ViewChild(MediaPreviewComponent, { read: ElementRef })
  public mediaPreviewRef?: ElementRef<HTMLElement>;

  private destroy$ = new Subject<void>();
  public mainMedia$ = new BehaviorSubject<Media | null>(null);
  public isOwner$ = new BehaviorSubject<boolean>(false);
  public imageBreakpoints = [
    normalBreakpoint,
    smallBreakpoint,
    xsmallBreakpoint,
    xxxsmallBreakpoint,
  ];

  public enablePictureInPicture$ = new BehaviorSubject<boolean>(false);

  public listMediaObjectFit$ = this.mainMedia$.pipe(
    map((media) => {
      if (!media || media.mediaType === MediaType.Image) {
        return MediaObjectFit.COVER;
      }
      return MediaObjectFit.CONTAIN;
    }),
  );

  public isVideoLikeContent$ = this.mainMedia$.pipe(
    map((mainMedia) => {
      if (!mainMedia) {
        return false;
      }

      return mainMedia.mediaType !== MediaType.Image;
    }),
  );

  @HostBinding('class.full-width')
  public get isFullWidth() {
    const media = this.mainMedia$.getValue();

    if (!media || media.mediaType === MediaType.Image) {
      return false;
    }

    return true;
  }

  public parsedMediaMetadata$ = this.mainMedia$.pipe(
    map((mainMedia) => {
      if (!mainMedia?.metadata) return;

      try {
        return JSON.parse(mainMedia.metadata);
      } catch (e) {
        return null;
      }
    }),
  );

  @Input()
  public set media(value: Media | null) {
    this.mainMedia$.next(value);
  }

  @Input()
  public listId: string | null = null;

  @Input()
  public isOwner = false;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngOnInit() {
    const rootElement = this.document.querySelector(ROOT_ELEMENT);

    if (rootElement) {
      fromEvent(rootElement, 'scroll')
        .pipe(
          debounceTime(50),
          map(() => this.mediaPreviewRef?.nativeElement),
          filter(isNonNullish),
          map((mediaPreviewNativeElement) =>
            mediaPreviewNativeElement.getBoundingClientRect(),
          ),
          pluck('bottom'),
          map((value) => value - NAVIGATION_BAR_HEIGHT < 0),
          takeUntil(this.destroy$),
        )
        .subscribe(this.enablePictureInPicture$);
    }
  }

  public onFullscreenChanged() {
    this.epicShowFullScreenChanged.emit();
  }
}
